import { createFromSchema } from "../Form/FormObject";
import {saveData, getData, clearData, findByID} from "../db/db_helper"
import {getProductById, updateProductInventory} from "./Products"
import {getDiscountById} from "./Discounts"
import {getExtraById} from "./Extras"
import { disableOffer } from "./Offers";

export const Orders = () => {
    // Order Schema Definition
    const orderSchema = {
        type: { type: 'string', default: 'ON_SPOT', required: true },
        paymentType: { type: 'string', default: null },
        orderProducts: { type: 'array', default: () => [] },
        coupons: { type: 'array', default: () => [] },
        discountId: { type: 'number', default: null },
        discount: { type: 'object', default: null },
        status: { type: 'string', default: null },
        orderId: { type: 'string', default: null },
        totalPrice: { type: 'number', default: null },
        isReady: { type: 'boolean', default: false },
        isFinal: { type: 'boolean', default: false },
        isPreview: { type: 'boolean', default: false },
        comment: { type: 'string', default: "" },
        preorderName: { type: 'string', default: "" }
    };

    const create = () => {
        return createFromSchema(orderSchema)
    }

    const load = (order) => {
        const newOrder = create()

        const rebuiltProducts = order.orderProducts.map(item => ({
            productId: item.productId,
            name: item.name,
            unitPrice: item.price,
            count: item.count,
            extras: item.extraDTOList || [],
            mwst: item.mwst || null,
            toGomwst: item.toGomwst || null,
            totalPrice: item.count * (item.price + (item.extraDTOList?.reduce((sum, extra) => sum + extra.price, 0) || 0))
        }));

        newOrder
            .set('paymentType', order.paymentType)
            .set('orderProducts', rebuiltProducts)
            .set('coupons', order.coupons || [])
            .set('isFinal', order.paymentType !== "UNPAYED")
            .set('isReady', false)
            .set('totalPrice', order.totalPrice)
            .set('discount', order.discountDTO || null)
            .set('discountId', order.discountDTO?.id || null)
            .set('status', order.status)
            .set('orderId', order.id)
            .set('type', order.type || 'ON_SPOT')
            .set('isPreview', false)
            .set('comment', order.comment || "")
            .set('preorderName', order.preorderName || "")

        return newOrder
    }

    const addProduct = (order, product, quantity) => {
        const currentProducts = order.get('orderProducts');

        const existingProductIndex = currentProducts.findIndex(
            (item) => item.productId === product.id && item.extras.length === 0
        );

        if (existingProductIndex !== -1) {
            order.updateInArray('orderProducts',
                (item) => item.productId === product.id && item.extras.length === 0,
                (item) => ({
                    ...item,
                    count: item.count + quantity,
                    totalPrice: (item.count + quantity) * product.price
                })
            );
        } else {
            order.addToArray('orderProducts', {
                productId: product.id,
                name: product.name,
                unitPrice: product.price,
                count: quantity,
                extras: [],
                mwst: product.mwst || null,
                totalPrice: quantity * product.price,
                toGomwst: product.toGoMwst || null
            });
        }
        return order
    }

    return {
        create,
        load,
        addProduct,
    }
}

//load from DB
export const getOnlineOrders = async () =>{
    return await getData("orders") || []
}

export const getOffllineOrders = async () =>{
    return await getData("sync_orders") || []
}

export const getAllOrders = async () =>{
    const online = await getOnlineOrders()
    const offline = await getOffllineOrders()
    const all = [...online, ...offline]
    return all.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
}

export const getHistoricOrders = async (startTime, endTime) =>{
    const data = await getData("historic_data") || []
    const start = new Date(startTime);
    const end = new Date(endTime);
    end.setHours(23, 59, 59, 999);
    return data.filter(order => {
        const orderTime = new Date(order.startTime);
        return orderTime >= start && orderTime <= end;
    });
}


export const getOrderById = async(id)=>{
    const online = await findByID("orders", id)
    const offline = await findByID("sync_orders", id)
    return online || offline || null
}

export const getOrdersByStatus = async(status) =>{
    const data = await getAllOrders()
    return data.filter(order => order.status === status);
}

export const getOrderIndex = (id, orderArray)=>{
    return orderArray.findIndex(o => o.id === id);
}

export const clearSyncOrders = async () =>{
    clearData("sync_orders")
}

export const saveOrders = async (obj) =>{
    saveData("orders", obj)
}

export const calculateOrderPrice = async (orderDTO) => {
    try {
        let totalPrice = 0;
        const products = orderDTO.orderProducts || orderDTO.productList || [];

        let discount = null;
        if (orderDTO.discountId) {
            let temp = await getDiscountById(orderDTO.discountId)
            if(temp.productStatus === 'ACTIVE'){ // Remove after DB Changes was made
                discount = temp
            }
        }

        for (const orderProduct of products) {
            const product = await getProductById(orderProduct.productId)
            if (!product) continue;

            let productPrice = product.price;

            if (product.reduceable && discount) {
                const discountAmount = productPrice / 100 * discount.percentage;
                productPrice = Math.round((productPrice - discountAmount) * 100) / 100;
            }

            const extraIds = orderProduct.extras.map(e => e.id);
            for (const extraId of extraIds) {
                let temp = await getExtraById(extraId)
                let extra = null
                if (temp.productStatus === "ACTIVE"){
                    extra = temp
                }
                if (extra) {
                    productPrice += extra.price;
                }
            }

            productPrice *= orderProduct.count;

            totalPrice += productPrice;
        }

        const coupons = orderDTO.coupons
        for (const coupon of coupons){
            const price = coupon.offerPrice;
            totalPrice += price
        }

        return totalPrice;

    } catch (error) {
        console.error("Fehler bei Preisberechnung:", error);
        return 0;
    }
}

export const createOrder = async (orderDTO) => {
    try {
        const order = await orderBuilder(orderDTO)
        const localData = await getOffllineOrders()
        let localList = [];

        if (localData) {
            localList = localData;
        }

        localList.push(order);
        await saveData("sync_orders", localList);

        return order;

    } catch (error) {
        console.error("Fehler bei offline Order Creation:", error);
        return null;
    }
}

export const updateOrder = async (orderDTO, id) => {
    try {
        const orderToCreate = await orderBuilder(orderDTO);
        const localData = await getOffllineOrders()

        let index = -1
        const serverData = await getOnlineOrders()
        index = await getOrderIndex(id, serverData)
        if(index !== -1){
            serverData.splice(index, 1)
            await saveData("orders", serverData)
            localData.push(orderToCreate)
            await saveData("sync_orders", localData)
            return true
        } 

        index = await getOrderIndex(id, localData)
        if(index !== -1){
            localData[index] = orderToCreate
            await saveData("sync_orders", localData)
            return true
        } 
        return false;

    } catch (error) {
        console.error("Fehler bei offline Order Creation:", error);
        return null;
    }
}

const updateOrderWODTO = async(id, order) =>{
    try {
        const localData = await getOffllineOrders()

        let index = -1
        const serverData = await getOnlineOrders()
        index = await getOrderIndex(id, serverData)
        if(index !== -1){
            serverData.splice(index, 1)
            await saveData("orders", serverData)
            localData.push(order)
            await saveData("sync_orders", localData)
            return true
        } 

        index = await getOrderIndex(id, localData)
        if(index !== -1){
            localData[index] = order
            await saveData("sync_orders", localData)
            return true
        } 
        return false;

    } catch (error) {
        console.error("Fehler bei offline Order Creation:", error);
        return null;
    }
}

export const updateOrderStatus = async (id, status) => {
    try {
        let index = -1
        let order = null
        const localData = await getOffllineOrders()
        const serverData = await getOnlineOrders()
        index = getOrderIndex(id, serverData)
        if(index !== -1){
            order = serverData[index]
        }

        index = getOrderIndex(id, localData)
        if(index !== -1){
            order = localData[index]
        }

        if(order){
            order.status = status
            if (status === "COMPLETE") {
                order.endTime = new Date().toISOString().slice(0, -1);

                if (order.startTime) {
                    const start = new Date(order.startTime);
                    const end = new Date(order.endTime);
                    const diffMs = end - start;
                    order.duration = msToIsoDuration(diffMs);
                }
            }
            await updateOrderWODTO(id, order)
            return true
        }
        return false
    } catch (error) {
        console.error("Fehler beim Aktualisieren des Bestellstatus:", error);
        return false;
    }
}

function msToIsoDuration(ms) {
    let seconds = ms / 1000;
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;

    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    const secondsStr = seconds.toFixed(9).replace(/0+$/, "");

    return `PT${hours > 0 ? hours + "H" : ""}${minutes > 0 ? minutes + "M" : ""}${secondsStr}S`;
}

const orderBuilder = async (orderDTO) => {
    try {
        const totalPrice = await calculateOrderPrice(orderDTO);

        const offlineId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const order = {
            ...orderDTO,
            offlineId: offlineId,
            totalPrice: totalPrice,
            orderStatus: orderDTO.orderStatus || "PENDING",
            startTime: new Date().toISOString().slice(0, -1)
        };

        for (const orderProduct of order.orderProducts) {
            const product = await getProductById(orderProduct.productId)
            if(product.stockManaged && product.availableStock){
                const calc = product.availableStock - orderProduct.count
                const newStock = calc >= 0 ? calc : 0
                await updateProductInventory(product.id, newStock)
            } 
        }

        if (order.coupons?.length) {
            await Promise.all(
                order.coupons.map((coupon) => disableOffer(coupon.id))
            );
        }

        return order;

    } catch (error) {
        console.error("Fehler bei offline Order Creation:", error);
        return null;
    }
}